import { ReactNode } from "react";
import Box from "@mui/material/Box";
import Grid, { GridBaseProps } from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

// Filter-row geometry shared by the two รายงานการโอน screens — the branch-day summary and
// the route-level drill-down. Both rows sit above tables the user reads as one report, so a
// field that is a different size or carries its label somewhere else on one screen reads as
// a different control rather than the same one.

/** Four fields on one desktop row — HO sees all of them, and a wrapped field made the bar
 * look ragged. */
export const FILTER_FIELD_SIZE: GridBaseProps["size"] = { xs: 12, sm: 6, md: 3 };

/**
 * Bottom spacing for the filter row. Sized for the floating helper text on the route-level
 * สายส่ง field, which contributes no height of its own — without the reservation
 * "เลือกสาขาก่อน" lands under the table header. The summary row keeps the same value so the
 * two screens put their table at the same place on the page.
 */
export const FILTER_ROW_BOTTOM_SPACING = 3.5;

/**
 * Lifts a field's helper text out of the flow so a field that has one (สายส่ง before a branch
 * is picked) stays exactly as tall as the fields that do not. It therefore contributes no
 * height of its own, which is the space FILTER_ROW_BOTTOM_SPACING above reserves.
 */
export const FLOATING_HELPER_TEXT_SX = {
  "& .MuiFormHelperText-root": {
    position: "absolute",
    top: "100%",
    left: 0,
    mx: 0,
    mt: 0.25,
  },
} as const;

interface FilterFieldProps {
  label: string;
  children: ReactNode;
}

/**
 * One labelled filter control. The label sits directly against its control rather than in a
 * fixed column, so it reads as belonging to the field instead of floating in the gap before
 * it.
 */
export default function FilterField({ label, children }: FilterFieldProps) {
  return (
    <Grid size={FILTER_FIELD_SIZE}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body2" noWrap>
          {label}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0, position: "relative" }}>{children}</Box>
      </Stack>
    </Grid>
  );
}
